import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CTEyeClientResponse, CompanyModel, GaugeConfig } from '../../../../data/entities';
import CTEyeService from '../../../../services/CTEyeService';
import CompanyService from '../../../../services/CompanyService';
import { showErrorMessage, showSuccessMessage } from '../../../../app/tools';
import { SlideOutModal, SpinnerDefault, Icon, IconDefinitions, InfoDetail, InfoSection, SectionHeader } from '../../../../components';
import sharedProperty from '../../../../app/sharedProperty';

interface EditClientModalProps {
  showState: sharedProperty<boolean>;
  client: CTEyeClientResponse | null;
  onSuccess: () => void;
}

const emptyToUndefined = (value: unknown, originalValue: unknown) =>
  originalValue === '' || originalValue == null ? undefined : value;

const schema = yup.object({
  description: yup.string().required('Device name is required'),
  companyId: yup.number().typeError('A company must be selected').required('A company must be selected'),
  logAllHeartbeats: yup.boolean(),
  monthlyInspectionLimit: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  monthlyReportLimit: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  cautionThreshold: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  criticalThreshold: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  capPercent: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  cautionFlawValue: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  criticalFlawValue: yup
    .number()
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater')
    .required('Required'),
  flwCautionDifference: yup
    .number()
    .transform(emptyToUndefined)
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0')
    .required('Required'),
  flwCriticalDifference: yup
    .number()
    .transform(emptyToUndefined)
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0')
    .required('Required')
    .test('flw-order', 'Must be greater than the yellow difference', function (value) {
      const caution = this.parent.flwCautionDifference;
      return value == null || caution == null || value > caution;
    }),
});

export function EditClientModal({ showState, client, onSuccess }: EditClientModalProps) {
  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const formik = useFormik({
    initialValues: {
      description: '',
      companyId: '' as number | '',
      logAllHeartbeats: false,
      monthlyInspectionLimit: '' as number | '',
      monthlyReportLimit: '' as number | '',
      cautionThreshold: '' as number | '',
      criticalThreshold: '' as number | '',
      capPercent: '' as number | '',
      cautionFlawValue: '' as number | '',
      criticalFlawValue: '' as number | '',
      flwCautionDifference: '' as number | '',
      flwCriticalDifference: '' as number | '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!client) return;
      setLoading(true);
      try {
        const success = await new CTEyeService().updateClient(client.id, {
          description: values.description.trim(),
          companyId: values.companyId as number,
          logAllHeartbeats: values.logAllHeartbeats ?? false,
          monthlyInspectionLimit: values.monthlyInspectionLimit as number,
          monthlyReportLimit: values.monthlyReportLimit as number,
          gaugeSettings: {
            cautionThreshold: values.cautionThreshold as number,
            criticalThreshold: values.criticalThreshold as number,
            capPercent: values.capPercent as number,
            cautionFlawValue: values.cautionFlawValue as number,
            criticalFlawValue: values.criticalFlawValue as number,
            flwCautionDifference: Number(values.flwCautionDifference),
            flwCriticalDifference: Number(values.flwCriticalDifference),
          },
        });
        if (success) {
          showSuccessMessage('Client updated successfully');
          showState.setter(false);
          onSuccess();
        } else {
          showErrorMessage('Failed to update client');
        }
      } catch (error) {
        console.error('Error updating client:', error);
        showErrorMessage('An error occurred while updating the client');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (showState.value) {
      loadCompanies();
      if (client) {
        populateForm(client);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showState.value]);

  const populateForm = async (clientData: CTEyeClientResponse) => {
    setLoadingConfig(true);
    let gauge: GaugeConfig | null = null;
    try {
      gauge = await new CTEyeService().getClientGaugeConfig(clientData.id);
    } catch (error) {
      console.error('Error loading gauge config:', error);
    }
    formik.resetForm({
      values: {
        description: clientData.description,
        companyId: clientData.companyId ?? '',
        logAllHeartbeats: clientData.isHeartbeatLoggingOn ?? false,
        monthlyInspectionLimit: clientData.monthlyInspectionLimit ?? '',
        monthlyReportLimit: clientData.monthlyReportLimit ?? '',
        cautionThreshold: gauge?.cautionThreshold ?? '',
        criticalThreshold: gauge?.criticalThreshold ?? '',
        capPercent: gauge?.capPercent ?? '',
        cautionFlawValue: gauge?.cautionFlawValue ?? '',
        criticalFlawValue: gauge?.criticalFlawValue ?? '',
        flwCautionDifference: gauge?.flwCautionDifference ?? '',
        flwCriticalDifference: gauge?.flwCriticalDifference ?? '',
      },
    });
    setLoadingConfig(false);
  };

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const data = await new CompanyService().getAllCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      showErrorMessage('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleCancel = () => {
    showState.setter(false);
  };

  if (!client) return null;

  const currentCompanyName = client.companyName || 'None';
  const newCompanyName = formik.values.companyId !== ''
    ? companies.find(c => c.id === Number(formik.values.companyId))?.name
    : 'None';
  const companyChanged = (client.companyId ?? '') !== formik.values.companyId;

  const clientDetails: InfoDetail[] = [
    {
      label: "Machine",
      value: client.description.split(' - ')[0],
      icon: <Icon iconName={IconDefinitions.chartLineUp} />,
    },
    {
      label: "User",
      value: client.description.split(' - ')[1] || '—',
      icon: <Icon iconName={IconDefinitions.user} />,
    },
    {
      label: "Version",
      value: <code>{client.runningVersion}</code>,
      icon: <Icon iconName={IconDefinitions.company} />,
    },
  ];

  return (
    <SlideOutModal
      showState={showState}
      title="Edit Client"
      footer={
        <div className="row m-0 w-100">
          <div className="col-6 ps-0">
            <Button
              onClick={handleCancel}
              variant="cancel"
              className="w-100"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
          <div className="col-6 pe-0">
            <Button
              onClick={() => formik.handleSubmit()}
              variant="primary"
              className="w-100"
              disabled={loading || loadingCompanies || loadingConfig}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      }
    >
      {loadingCompanies || loadingConfig ? (
        <div className="text-center py-4">
          <SpinnerDefault />
        </div>
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <InfoSection title="Client Information" details={clientDetails} />

          {/* General Settings */}
          <div className="mb-3">
            <SectionHeader title="General" />
            <div className="px-3 pt-3">
              <Form.Group className="mb-3">
                <Form.Label>Device Name</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter device name"
                  disabled={loading}
                  isInvalid={formik.touched.description && !!formik.errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.description}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Assign to Company</Form.Label>
                <Form.Select
                  name="companyId"
                  value={formik.values.companyId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  isInvalid={formik.touched.companyId && !!formik.errors.companyId}
                >
                  <option value="" disabled>-- Select a Company --</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.companyId}
                </Form.Control.Feedback>
              </Form.Group>

              {companyChanged && formik.values.companyId !== '' && (
                <div className="alert alert-warning mb-3 p-2">
                  <small className="d-flex">
                    <span className="me-1 flex-shrink-0">
                      <Icon iconName={IconDefinitions.circleInfo} />
                    </span>
                    <span>
                      {currentCompanyName} → <strong>{newCompanyName}</strong>
                    </span>
                  </small>
                </div>
              )}

             <Form.Group className="mb-0">
                <Form.Label>Log all heartbeats</Form.Label>
                <div className="p-3 bg-light rounded d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="logAllHeartbeats"
                    name="logAllHeartbeats"
                    checked={formik.values.logAllHeartbeats}
                    onChange={formik.handleChange}
                    disabled={loading}
                    label={formik.values.logAllHeartbeats ? 'Enabled' : 'Disabled'}
                  />
                </div>
              </Form.Group>
            </div>
          </div>

          {/* Subscription Limits */}
          <div className="mb-3">
            <SectionHeader title="Subscription Limits" />
            <div className="px-3 pt-3">
              <Form.Group className="mb-3">
                <Form.Label>Monthly Inspection Limit</Form.Label>
                <Form.Control
                  type="number"
                  name="monthlyInspectionLimit"
                  value={formik.values.monthlyInspectionLimit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onFocus={(e) => e.target.select()}
                  placeholder="e.g. 100"
                  disabled={loading}
                  isInvalid={formik.touched.monthlyInspectionLimit && !!formik.errors.monthlyInspectionLimit}
                />
                {formik.touched.monthlyInspectionLimit && formik.errors.monthlyInspectionLimit ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.monthlyInspectionLimit}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Text className="text-muted">
                    Maximum inspections allowed per month (0 = unlimited)
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>Monthly Report Limit</Form.Label>
                <Form.Control
                  type="number"
                  name="monthlyReportLimit"
                  value={formik.values.monthlyReportLimit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onFocus={(e) => e.target.select()}
                  placeholder="e.g. 50"
                  disabled={loading}
                  isInvalid={formik.touched.monthlyReportLimit && !!formik.errors.monthlyReportLimit}
                />
                {formik.touched.monthlyReportLimit && formik.errors.monthlyReportLimit ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.monthlyReportLimit}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Text className="text-muted">
                    Maximum reports allowed per month (0 = unlimited)
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Gauge Settings */}
          <div className="mb-3">
            <SectionHeader title="Gauge Settings" />
            <div className="px-3 pt-3">
              <div className="row">
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Caution Threshold (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="cautionThreshold"
                      value={formik.values.cautionThreshold}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.cautionThreshold && !!formik.errors.cautionThreshold}
                    />
                    {formik.touched.cautionThreshold && formik.errors.cautionThreshold ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.cautionThreshold}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Wall-loss % for caution zone</Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Critical Threshold (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="criticalThreshold"
                      value={formik.values.criticalThreshold}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.criticalThreshold && !!formik.errors.criticalThreshold}
                    />
                    {formik.touched.criticalThreshold && formik.errors.criticalThreshold ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.criticalThreshold}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Wall-loss % for critical zone</Form.Text>
                    )}
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Cap Percent (%)</Form.Label>
                <Form.Control
                  type="number"
                  name="capPercent"
                  value={formik.values.capPercent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onFocus={(e) => e.target.select()}
                  disabled={loading}
                  isInvalid={formik.touched.capPercent && !!formik.errors.capPercent}
                />
                {formik.touched.capPercent && formik.errors.capPercent ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.capPercent}
                  </Form.Control.Feedback>
                ) : (
                  <Form.Text className="text-muted">Maximum display percentage on gauge</Form.Text>
                )}
              </Form.Group>

              <div className="row">
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Caution Flaw Value</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="cautionFlawValue"
                      value={formik.values.cautionFlawValue}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.cautionFlawValue && !!formik.errors.cautionFlawValue}
                    />
                    {formik.touched.cautionFlawValue && formik.errors.cautionFlawValue ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.cautionFlawValue}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Raw sensor value for caution</Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Critical Flaw Value</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="criticalFlawValue"
                      value={formik.values.criticalFlawValue}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.criticalFlawValue && !!formik.errors.criticalFlawValue}
                    />
                    {formik.touched.criticalFlawValue && formik.errors.criticalFlawValue ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.criticalFlawValue}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Raw sensor value for critical</Form.Text>
                    )}
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>

          {/* FLW Rolling Average Settings */}
          <div className="mb-3">
            <SectionHeader title="FLW Rolling Average" />
            <div className="px-3 pt-3">
              <div className="row">
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Yellow Difference (YY)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="flwCautionDifference"
                      value={formik.values.flwCautionDifference}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.flwCautionDifference && !!formik.errors.flwCautionDifference}
                    />
                    {formik.touched.flwCautionDifference && formik.errors.flwCautionDifference ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.flwCautionDifference}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Deviation from rolling avg for a yellow flag</Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Red Difference (YR)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="flwCriticalDifference"
                      value={formik.values.flwCriticalDifference}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => e.target.select()}
                      disabled={loading}
                      isInvalid={formik.touched.flwCriticalDifference && !!formik.errors.flwCriticalDifference}
                    />
                    {formik.touched.flwCriticalDifference && formik.errors.flwCriticalDifference ? (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.flwCriticalDifference}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">Deviation from rolling avg for a red flag</Form.Text>
                    )}
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </SlideOutModal>
  );
}

export default EditClientModal;